import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const AuthContext = createContext();
const AUTH_STORAGE_KEY = 'barsconnect_auth_token';

const safeGetStoredToken = () => {
  try {
    return localStorage.getItem(AUTH_STORAGE_KEY);
  } catch (error) {
    return null;
  }
};

const safeStoreToken = (token) => {
  try {
    if (token) {
      localStorage.setItem(AUTH_STORAGE_KEY, token);
    } else {
      localStorage.removeItem(AUTH_STORAGE_KEY);
    }
  } catch (error) {
    // no-op for private mode/storage restrictions
  }
};

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(safeGetStoredToken());
  const [loading, setLoading] = useState(true);

  const authReady = Boolean(token && user && !loading);

  useEffect(() => {
    let isActive = true;

    const syncCurrentUser = async () => {
      try {
        const response = await axios.get('/auth/me');
        if (!isActive) return;
        setUser(response.data);
      } catch (error) {
        if (!isActive) return;
        console.error('Failed to fetch user:', error);
        safeStoreToken(null);
        delete axios.defaults.headers.common['Authorization'];
        setToken(null);
        setUser(null);
      } finally {
        if (isActive) {
          setLoading(false);
        }
      }
    };

    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      syncCurrentUser();
    } else {
      delete axios.defaults.headers.common['Authorization'];
      setLoading(false);
    }

    return () => {
      isActive = false;
    };
  }, [token]);

  const login = async (email, password) => {
    setLoading(true);
    try {
      const response = await axios.post('/auth/login', { email, password });
      const { token: newToken, user: userData } = response.data;
      
      if (newToken) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      } else {
        delete axios.defaults.headers.common['Authorization'];
      }
      safeStoreToken(newToken || null);
      setToken(newToken || null);
      setUser(userData);
      setLoading(false);
      
      toast.success('Welcome back!');
      return { success: true, user: userData };
    } catch (error) {
      setLoading(false);
      const message = error.response?.data?.detail || 'Login failed';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const register = async (userData) => {
    setLoading(true);
    try {
      const response = await axios.post('/auth/register', userData);
      const { token: newToken, user: newUser } = response.data;
      
      if (newToken) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      } else {
        delete axios.defaults.headers.common['Authorization'];
      }
      safeStoreToken(newToken || null);
      setToken(newToken || null);
      setUser(newUser);
      setLoading(false);
      
      toast.success('Account created successfully!');
      return { success: true, user: newUser };
    } catch (error) {
      setLoading(false);
      const message = error.response?.data?.detail || 'Registration failed';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const logout = () => {
    axios.post('/auth/logout').catch(() => {});
    safeStoreToken(null);
    setToken(null);
    setUser(null);
    delete axios.defaults.headers.common['Authorization'];
    toast.success('Logged out successfully');
  };

  const value = {
    user,
    setUser,
    token,
    login,
    register,
    logout,
    loading,
    authReady,
    isBarOwner: user?.user_type === 'bar_owner',
    isBartender: user?.user_type === 'bartender'
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
